import {
  commitUpdateLabelsForLabelableMutation,
  computeLabelDiff,
} from '@github-ui/item-picker/commitUpdateLabelsForLabelableMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {getMostUsedLabels, recordLabelsUsage} from '@github-ui/item-picker-label-tsq/favorite-labels-storage'
import {type Label, LabelsList as Labels} from '@github-ui/labels-list'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo} from 'react'
import {useRelayEnvironment} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {IssueNode, IssueNodeRepository} from '../issue-viewer-view-query-types'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'

export const ReadonlyLabelsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />

type LabelsSectionProps = {
  sectionHeader: React.ReactElement
  labels: Label[]
}
export const LabelsSection = ({sectionHeader, labels}: LabelsSectionProps) => {
  return (
    <Section
      id={`sidebar-labels-section`}
      sectionHeader={sectionHeader}
      emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}
    >
      <Labels labels={labels} testId={TEST_IDS.issueLabels} />
    </Section>
  )
}

type LabelsSectionIssueRepository = Pick<IssueNodeRepository, 'name' | 'isArchived'> & {
  readonly owner: Pick<NonNullable<IssueNodeRepository['owner']>, 'login'>
}

export type EditIssueLabelsSectionFragment = Pick<IssueNode, 'id' | 'labels' | 'viewerCanLabel'> & {
  readonly repository: LabelsSectionIssueRepository
}

// RELAY-CLEANUP(LabelsSectionAssignedLabels): widens nullable fields so still-Relay parent data is
// assignable. Remove when the remaining Relay parents leave Relay.
export type LabelsSectionAssignedLabelsFragment = RelayCompat<Pick<IssueNode, 'labels'>>

export type EditIssueLabelsSectionProps = {
  onIssueUpdate?: () => void
  issue: EditIssueLabelsSectionFragment
  canCreateLabel?: boolean
  insideSidePanel?: boolean
  readonly: boolean
}

export function EditIssueLabelsSection({
  issue: data,
  canCreateLabel = false,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueLabelsSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
  } = data

  const {id: issueId, viewerCanLabel} = data

  const labels = useMemo(() => data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || [], [data.labels])

  const environment = useRelayEnvironment()
  const readonlyLabel = readonly || !viewerCanLabel || isRepositoryArchived

  const {addToast} = useToastContext()

  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const onSuccess = () => {
        if (selectedLabels.length > 0) {
          recordLabelsUsage(
            `${owner}/${repo}`,
            selectedLabels.map(label => label.id),
          )
        }
        onIssueUpdate?.()
      }

      const onErrorHandler = () =>
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateLabels,
        })

      // Use diff-based mutation to avoid race conditions with concurrent edits
      const {labelsToAdd, labelsToRemove} = computeLabelDiff(labels, selectedLabels)

      // If no changes, skip the mutation
      if (labelsToAdd.length === 0 && labelsToRemove.length === 0) {
        return
      }

      // Derive optimistic final labels from current store labels (which may include
      // concurrent additions from other users) by applying the diff, rather than
      // using selectedLabels which only reflects this user's picker state.
      const removeIds = new Set(labelsToRemove.map(l => l.id))
      const currentWithoutRemoved = labels.filter(l => !removeIds.has(l.id))
      const existingIds = new Set(currentWithoutRemoved.map(l => l.id))
      const optimisticLabels = [...currentWithoutRemoved, ...labelsToAdd.filter(l => !existingIds.has(l.id))]

      commitUpdateLabelsForLabelableMutation({
        environment,
        input: {
          labelableId: issueId,
          labelsToAdd,
          labelsToRemove,
          labelableTypeName: 'Issue',
        },
        expectedFinalLabels: optimisticLabels,
        onError: onErrorHandler,
        onCompleted: onSuccess,
      })
    },
    [addToast, environment, issueId, labels, onIssueUpdate, owner, repo],
  )

  const labelSectionHeader = useMemo(() => {
    if (readonlyLabel) {
      return <ReadonlyLabelsSectionHeader />
    }

    const favoriteLabelIds = getMostUsedLabels(`${owner}/${repo}`).map(entry => entry.labelId)

    return (
      <LabelPicker
        repo={repo}
        owner={owner}
        readonly={readonlyLabel}
        labels={labels}
        canCreateLabel={canCreateLabel}
        favoriteLabelIds={favoriteLabelIds}
        enableSuggestedLabels
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader
            title={LABELS.sectionTitles.labels}
            buttonProps={{
              ...anchorProps,
              onClick: e => {
                sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABELS_SECTION_EDIT_CLICK')
                anchorProps.onClick?.(e)
              },
            }}
            ref={ref}
          />
        )}
        insidePortal={insideSidePanel}
        showNoMatchItem
      />
    )
  }, [readonlyLabel, owner, repo, labels, canCreateLabel, onSelectionChanged, insideSidePanel, sendAnalyticsEvent])

  return <LabelsSection sectionHeader={labelSectionHeader} labels={labels} />
}
